// Centralized translation dictionary. Keep keys stable; English values are the source of truth.
export const TRANSLATIONS = {
  en: {
    // Common
    "lang.label": "Language",
    "lang.english": "English",
    "lang.spanish": "Español",
    "se_habla": "We speak Spanish · Hablamos español",
    "free_review": "Free Case Review",
    "back": "Back",
    "continue": "Continue",
    "submit": "Submit",
    "loading": "Loading…",
    "close": "Close",
    "save": "Save",
    "cancel": "Cancel",

    // Nav
    "nav.case_value": "Case Value",
    "nav.chat": "Chat Intake",
    "nav.hotline": "24/7 Hotline",
    "nav.sign_in": "Sign in",
    "nav.my_cases": "My Cases",
    "nav.find_care": "Find Care",

    // Urgency banner
    "urgency.banner": "NY statute: 3 years · NJ statute: 2 years — every day matters. Free review in 60 seconds.",

    // Hero
    "hero.pill": "NY · NJ accident victims",
    "hero.h1.line1": "Hit on the road?",
    "hero.h1.line2": "Get the settlement",
    "hero.h1.line3": "you actually deserve.",
    "hero.subhead": "Apex connects motor-vehicle accident victims across New York and New Jersey with vetted personal-injury attorneys. Free review. No upfront fees. Available 24/7 — even from your hospital bed.",
    "hero.cta.intake": "Get Your Free Case Review",
    "hero.cta.estimator": "Estimate My Case Value",
    "hero.bullet.2min": "2-minute intake",
    "hero.bullet.nofee": "No fee unless we win",
    "hero.bullet.confidential": "100% confidential",
    "hero.stats.title": "By the numbers",
    "hero.stat.recovered": "Recovered for clients",
    "hero.stat.cases": "Cases handled",
    "hero.stat.rating": "Avg. client rating",
    "hero.stat.hotline": "Intake hotline",
    "hero.help_now": "Need help right now?",

    // Trust strip
    "trust.featured": "As featured in",

    // Tools quick nav
    "tools.intake.title": "Free Case Review",
    "tools.intake.desc": "2-min secure form. Submit anytime.",
    "tools.intake.cta": "Start now",
    "tools.estimator.title": "AI Case-Value Estimator",
    "tools.estimator.desc": "Conservative settlement range in seconds.",
    "tools.estimator.cta": "Estimate value",
    "tools.chat.title": "Chat With Avery",
    "tools.chat.desc": "Talk through your accident with our AI intake.",
    "tools.chat.cta": "Open chat",
    "tools.find_care.title": "Find a Doctor",
    "tools.find_care.desc": "Orthopedic & spine match for any injury — not just accidents.",
    "tools.find_care.cta": "Get matched",

    // Find Care promo (Landing section)
    "findcare.section_label": "Hurt but not from a crash?",
    "findcare.section_title": "We also help you find orthopedic and spine doctors who take your insurance.",
    "findcare.section_body": "Tell us your symptoms and your insurance carrier. We'll connect you with a trusted orthopedic or spine provider in New York or New Jersey — typically within one business day. Open to everyone, accident-related or not.",
    "findcare.section_cta": "Find Care Now",
    "findcare.tag.private": "Encrypted & private",
    "findcare.tag.insurance": "Most major insurance accepted",
    "findcare.preview.label": "How it works",
    "findcare.preview.item1": "Check off your symptoms (back, neck, knee, etc.).",
    "findcare.preview.item2": "Pick your insurance carrier from a list.",
    "findcare.preview.item3": "We match you with an orthopedic or spine doctor.",
    "findcare.preview.item4": "Coordinator calls you within 1 business day.",
    "findcare.preview.privacy_title": "Privacy first",
    "findcare.preview.privacy_body": "We never ask for your member ID. We never sell your information. Your details are used only to match you with care.",

    // Track section
    "track.label": "Your case, always in view",
    "track.title": "Sign in to track your case status in real time.",
    "track.body": "Every submission is connected to your own private portal. See exactly where your case stands — from intake to attorney match to settlement — without making a single phone call.",
    "track.cta_signup": "Create my account",
    "track.cta_login": "Sign in",
    "track.case_id_label": "Case",
    "track.status.submitted": "Submitted",
    "track.status.contacted": "Attorney contacted",
    "track.status.accepted": "Case accepted",
    "track.status.resolved": "Resolved",
    "track.latest_update": "Latest update · 2 days ago",
    "track.latest_body": "Your assigned attorney has reviewed your medical records and will call you tomorrow before 5 PM EST.",

    // How it works
    "how.label": "How it works",
    "how.title": "From accident to settlement in three clear steps.",
    "how.step1.title": "Tell us what happened",
    "how.step1.desc": "Submit a 2-minute intake or chat with Avery, our AI assistant. We capture only what's needed.",
    "how.step2.title": "We match you",
    "how.step2.desc": "Within 1 business hour, a vetted NY/NJ personal-injury attorney reviews your case — free.",
    "how.step3.title": "Get paid",
    "how.step3.desc": "If you don't win, you pay nothing. Average settlement turnaround: 4–9 months.",

    // Why us
    "why.label": "We understand",
    "why.title": "You shouldn't have to fight the insurance company alone.",
    "why.body": "After a crash on the BQE, the Turnpike, or anywhere across NY/NJ, the calls start fast and the offers are insultingly low. We're here so you can focus on healing while a real attorney handles every conversation, deadline, and lien on your behalf.",
    "why.feature.vetted": "Vetted attorneys",
    "why.feature.response": "1-hour response",
    "why.feature.contingency": "Contingency only",

    // Testimonial
    "testimonial.quote": "I was rear-ended on the LIE with two herniated discs and no idea what to do. Apex connected me to an attorney the same afternoon. Eight months later I had a six-figure settlement and zero stress.",
    "testimonial.attribution": "— Marcus T., Queens NY · auto collision, 2024",

    // FAQ
    "faq.label": "FAQ",
    "faq.title": "Common questions, straight answers.",
    "faq.q1": "How much does it cost to talk to you?",
    "faq.a1": "Nothing. The case review and attorney match are 100% free. If your attorney takes the case, you only pay if you win — standard contingency, no upfront cost.",
    "faq.q2": "What if the accident wasn't my fault entirely?",
    "faq.a2": "NY and NJ both allow recovery even if you share fault (comparative negligence). We still want to hear your story.",
    "faq.q3": "I already saw a lawyer. Can I switch?",
    "faq.a3": "Yes. You're free to change representation at any time. We can help you find a stronger fit.",
    "faq.q4": "Is my information private?",
    "faq.a4": "Absolutely. Submissions are encrypted, never sold, and only shared with the vetted attorney reviewing your case.",
    "faq.q5": "How long do I have to file?",
    "faq.a5": "In NY the statute of limitations is generally 3 years; in NJ it's 2. Don't wait — evidence fades fast.",

    // Final CTA
    "final.title": "Hurt in a crash? Don't wait.",
    "final.body": "Most NY/NJ MVA claims are settled within 12 months when filed early. Let's get yours started.",
    "final.cta.intake": "Start Free Case Review",
    "final.cta.chat": "Chat With Avery",

    // Footer
    "footer.about": "We connect motor vehicle accident victims across New York and New Jersey with experienced personal-injury attorneys — at no upfront cost. Available 24/7.",
    "footer.tag.ny": "NY licensed",
    "footer.tag.nj": "NJ licensed",
    "footer.tag.no_fee": "No fee unless we win",
    "footer.tag.spanish": "Hablamos español",
    "footer.action.title": "Take Action",
    "footer.action.intake": "Start Free Case Review",
    "footer.action.estimator": "AI Case-Value Estimator",
    "footer.action.chat": "Talk to Avery (AI Intake)",
    "footer.action.call": "Call 24/7 Hotline",
    "footer.legal.title": "Legal",
    "footer.legal.advertising": "Attorney advertising",
    "footer.legal.no_guarantee": "Past results don't guarantee future outcomes",
    "footer.legal.consult": "Prior consultation required before representation",
    "footer.legal.admin": "Admin Sign In",
    "footer.rights": "All rights reserved.",
    "footer.serving": "Serving New York & New Jersey · Available 24/7",

    // Sticky mobile CTA
    "mobile.call": "Call",

    // Lead intake form
    "intake.label": "Free case review",
    "intake.title": "Tell us about your accident.",
    "intake.subtitle": "Takes ~2 minutes. We only ask what's needed.",
    "intake.step": "Step {n} of {total} · {label}",
    "intake.step.accident": "Accident",
    "intake.step.injuries": "Injuries",
    "intake.step.contact": "Contact",
    "intake.encrypted": "Encrypted",
    "intake.state_label": "State of accident",
    "intake.state_ny": "New York",
    "intake.state_nj": "New Jersey",
    "intake.date_label": "Date of accident",
    "intake.city_label": "City / borough",
    "intake.city_placeholder": "e.g. Brooklyn, Newark",
    "intake.type_label": "Accident type",
    "intake.select": "Select…",
    "intake.type.rear_end": "Rear-end collision",
    "intake.type.head_on": "Head-on collision",
    "intake.type.tbone": "T-bone / side-impact",
    "intake.type.pileup": "Multi-vehicle pileup",
    "intake.type.hit_run": "Hit and run",
    "intake.type.pedestrian": "Pedestrian struck",
    "intake.type.motorcycle": "Motorcycle accident",
    "intake.type.rideshare": "Rideshare (Uber/Lyft)",
    "intake.type.truck": "Truck / 18-wheeler",
    "intake.type.other": "Other",
    "intake.fault_label": "Who do you believe was at fault?",
    "intake.fault.other": "Other driver",
    "intake.fault.me": "Me",
    "intake.fault.shared": "Shared fault",
    "intake.fault.unsure": "Not sure",
    "intake.police_label": "A police report was filed",
    "intake.injuries_label": "Describe your injuries",
    "intake.injuries_placeholder": "Neck pain, herniated discs, fractures, etc.",
    "intake.treatment_label": "I've received medical treatment for these injuries",
    "intake.has_attorney_label": "I already have an attorney on this case",
    "intake.notes_label": "Anything else we should know?",
    "intake.notes_placeholder": "Optional. Witnesses, vehicle damage, prior injuries…",
    "intake.name_label": "Full name",
    "intake.email_label": "Email",
    "intake.phone_label": "Phone",
    "intake.phone_placeholder": "(555) 555-1234",
    "intake.consent": "By submitting you agree to be contacted by Apex MVA and a partner attorney. Your information is confidential and never sold.",
    "intake.submit": "Submit Free Review",
    "intake.submitting": "Submitting…",
    "intake.success.title": "We've got it. Take a breath.",
    "intake.success.body": "A vetted personal-injury attorney licensed in {state} will review your case and contact you within 1 business hour. If your situation is urgent, call our 24/7 hotline.",
    "intake.success.call": "Call",
    "intake.success.home": "Back to Home",
    "intake.toast.success": "Case review submitted. We'll be in touch shortly.",

    // Chatbot
    "chat.label": "AI Intake Assistant",
    "chat.title": "Chat with Avery.",
    "chat.subtitle": "A judgment-free, encrypted conversation. Avery isn't a lawyer — but she can help you organize the facts so we can match you faster.",
    "chat.header.subtitle": "Apex MVA · AI intake · always confidential",
    "chat.input_placeholder": "Type what happened…",
    "chat.first_message": "Hi, I'm Avery — Apex's intake assistant. I'm really sorry you're dealing with this. Whenever you're ready, can you tell me a little about what happened?",
    "chat.skip": "Skip chat — submit the secure form instead",
    "chat.or_call": "Or call",
    "chat.error": "Sorry, I had trouble responding. Please try again or call our hotline.",

    // Estimator
    "est.label": "AI Case-Value Estimator",
    "est.title": "Get a fast read on your case — powered by AI.",
    "est.body": "Enter your accident details. We'll generate a conservative settlement range so you walk into your attorney call with realistic expectations.",
    "est.not_advice": "Not legal advice.",
    "est.injuries_label": "Injuries sustained",
    "est.injuries_placeholder": "e.g. whiplash, herniated L5-S1, concussion, broken wrist",
    "est.fault_label": "Fault",
    "est.treatment_label": "Received medical treatment",
    "est.notes_label": "Additional details (optional)",
    "est.notes_placeholder": "Lost wages, ongoing therapy, vehicle damage, etc.",
    "est.submit": "Estimate My Case Value",
    "est.analyzing": "Analyzing…",
    "est.placeholder.body": "Your estimated settlement range will appear here. Most analyses take 5–10 seconds.",
    "est.placeholder.loading": "Avery is reviewing your case…",
    "est.range_label": "Estimated settlement range",
    "est.strength_label": "Case strength:",
    "est.cta_match": "Get matched with an attorney",
    "est.disclaimer_fallback": "Estimates are general guidance only and not legal advice.",

    // Login
    "login.back": "Back to site",
    "login.tagline.admin": "Lead management for personal-injury intake teams in New York & New Jersey.",
    "login.tagline.claimant": "Track your case, follow status updates, and stay in the loop with your attorney match.",
    "login.footer.admin": "Authorized personnel only.",
    "login.footer.claimant": "Your information is encrypted and confidential.",
    "login.title.admin": "Admin Sign In",
    "login.title.claimant": "Sign in to track your case",
    "login.subtitle.admin": "Access the lead pipeline dashboard.",
    "login.subtitle.claimant": "Welcome back. Let's check on your case.",
    "login.submit": "Sign In",
    "login.submitting": "Signing in…",
    "login.new_here": "New here?",
    "login.create_account": "Create an account",

    // Register
    "reg.title_panel": "Create your case account.",
    "reg.tagline_panel": "Track status updates, attach documents, and message your assigned attorney — all in one place.",
    "reg.bullet.encrypted": "100% encrypted & confidential",
    "reg.bullet.nofee": "No fees unless we win",
    "reg.bullet.licensed": "NY & NJ licensed partners",
    "reg.never_sold": "Information shared here is never sold.",
    "reg.title": "Create account",
    "reg.subtitle": "Takes 30 seconds. Already submitted a case? We'll link it automatically.",
    "reg.name": "Full name",
    "reg.email": "Email",
    "reg.phone": "Phone (optional)",
    "reg.password": "Password",
    "reg.password_too_short": "Password must be at least 6 characters.",
    "reg.submit": "Create account",
    "reg.submitting": "Creating…",
    "reg.have_account": "Already have an account?",
    "reg.sign_in": "Sign in",

    // MyCases
    "mc.label": "Your portal",
    "mc.welcome": "Welcome, {name}.",
    "mc.subtitle": "Here's the status of every case you've submitted with Apex.",
    "mc.new_case": "Submit new case",
    "mc.logout": "Sign out",
    "mc.loading": "Loading your cases…",
    "mc.empty.title": "No cases yet",
    "mc.empty.body": "Start a free case review and we'll match you with a vetted NY/NJ attorney.",
    "mc.empty.cta": "Start your free case review",
    "mc.tab.overview": "Overview",
    "mc.tab.treatments": "Treatments",
    "mc.tab.documents": "Documents",
    "mc.tab.messages": "Messages",
    "mc.detail.submitted": "Submitted",
    "mc.detail.fault": "At fault",
    "mc.detail.injuries": "Injuries",
    "mc.detail.notes": "Notes",
    "mc.detail.call": "Call your intake team",
    "mc.status.new": "Submitted",
    "mc.status.contacted": "Attorney contacted",
    "mc.status.qualified": "Case accepted",
    "mc.status.closed": "Resolved",
    "mc.status.rejected": "Not a fit",
    "mc.rejected_msg": "Unfortunately your case wasn't a match this time. Our team can refer you to other resources — reach out anytime.",
    "mc.fallback_title": "Motor vehicle accident",

    // Treatments
    "tx.total": "Total visits",
    "tx.completed": "Completed",
    "tx.physicians": "Physicians",
    "tx.record": "Treatment record",
    "tx.add": "Add visit",
    "tx.cancel": "Cancel",
    "tx.physician": "Physician name",
    "tx.physician_placeholder": "Dr. Last Name",
    "tx.specialty": "Specialty",
    "tx.specialty_placeholder": "Orthopedist, Chiropractor…",
    "tx.facility": "Facility / clinic",
    "tx.facility_placeholder": "NY Spine Center",
    "tx.visit_date": "Visit date",
    "tx.visit_type": "Visit type",
    "tx.notes": "Notes",
    "tx.notes_placeholder": "Diagnosis, prescriptions, next steps…",
    "tx.occurred": "Visit already occurred (uncheck if scheduled / upcoming)",
    "tx.save": "Save visit",
    "tx.saving": "Saving…",
    "tx.loading": "Loading treatment history…",
    "tx.empty": "No visits logged yet. Use {add_visit} above to start your treatment record.",
    "tx.empty.add_visit": "Add visit",
    "tx.upcoming": "Upcoming",
    "tx.added_by_team": "Apex team",
    "tx.added_by_you": "you",
    "tx.required": "Physician name is required.",
    "tx.toast.saved": "Visit added to your record.",
    "tx.toast.removed": "Visit removed.",
    "tx.delete_confirm": "Delete visit with {physician} on {date}?",
    "tx.visit_count_one": "{n} visit",
    "tx.visit_count_other": "{n} visits",

    // Documents
    "docs.add.title": "Add a document",
    "docs.add.body": "Police reports, medical bills, photos. PDF/JPG/PNG/HEIC up to 10 MB.",
    "docs.upload": "Upload file",
    "docs.uploading": "Uploading…",
    "docs.loading": "Loading documents…",
    "docs.empty": "No documents yet.",
    "docs.view": "View",
    "docs.uploaded_by": "uploaded by",
    "docs.uploader.team": "Apex team",
    "docs.uploader.you": "you",
    "docs.too_large": "File too large (10MB max).",
    "docs.uploaded": "Uploaded.",
    "docs.deleted": "Deleted.",
    "docs.delete_confirm": "Delete \"{filename}\"?",

    // Messages
    "msg.thread.claimant": "Message thread with your Apex intake team",
    "msg.thread.admin": "Message thread with claimant",
    "msg.loading": "Loading…",
    "msg.empty.claimant": "Your Apex intake team will message you here when updates land.",
    "msg.empty.admin": "No messages yet — start the conversation.",
    "msg.input.claimant": "Type a message to your intake team…",
    "msg.input.admin": "Reply to claimant…",
    "msg.sender.you": "You",
    "msg.sender.team": "Apex Intake Team",
    "msg.sender.claimant": "Claimant",

    // Speed-to-lead promise
    "speed.promise": "We call you within 5 minutes — or your case review is free.",

    // Social proof ticker
    "social.action.intake": "started a free case review",
    "social.action.estimate": "got an AI case estimate",
    "social.action.match": "got matched with an attorney",
    "social.action.settled": "received a settlement",

    // Statute countdown
    "statute.title": "Time left to file your claim",
    "statute.expired": "Statute of limitations has expired — call us anyway, we may still help.",
    "statute.urgent": "Urgent — file as soon as possible.",
    "statute.years_one": "{n} year",
    "statute.years_other": "{n} years",
    "statute.months_one": "{n} month",
    "statute.months_other": "{n} months",
    "statute.days_one": "{n} day",
    "statute.days_other": "{n} days",

    // Voice intake
    "voice.intake.cta": "Tap to speak instead",
    "voice.recording": "Recording — tap to stop",
    "voice.transcribing": "Transcribing your story…",
    "voice.transcribed": "Got it — review and edit below.",
    "voice.error": "Could not transcribe. Please type your story instead.",
    "voice.permission_denied": "Microphone access blocked. Please type instead.",

    // Magic link
    "magic.tab.password": "Use password",
    "magic.tab.magic": "Email me a link",
    "magic.subtitle": "Get a one-time login link via email (and SMS if we have your number). No password needed.",
    "magic.email_placeholder": "you@email.com",
    "magic.send": "Send me a magic link",
    "magic.sending": "Sending…",
    "magic.sent.title": "Check your email and phone.",
    "magic.sent.body": "If an account exists for {email}, you'll get a one-time login link within 1 minute. It expires in 15 minutes.",
    "magic.sent.again": "Send another link",
    "magic.verify.title": "Signing you in…",
    "magic.verify.success": "Welcome back. Redirecting…",
    "magic.verify.error": "This link is invalid or has expired. Request a new one.",
    "magic.verify.cta": "Request a new link",

    // Sticky mobile CTA
    "mobile.text": "Text Us",

    // City landing
    "city.headline": "Hurt in a {city} accident?",
    "city.subhead": "Free case review · No fee unless we win · NY and NJ licensed attorneys, available 24/7.",
    "city.local_pill": "Local · {city}",
    "city.body": "Our network of vetted personal-injury attorneys recovers millions for {city} accident victims every year. From the {landmarks} — we know the roads, the courts, and the insurers.",
    "city.cta_intake": "Start Free Case Review",
    "city.cta_chat": "Chat With Avery",
    "city.trust1": "1-hour response · 24/7",
    "city.trust2": "Hablamos español",
    "city.trust3": "No upfront cost",

    // Common
    "yes": "Yes",
    "no": "No",

    // Nav extras
    "nav.attorney_portal": "Attorney portal",

    // Case stages (shared with admin/attorney/claimant)
    "stage.intake": "Intake",
    "stage.investigation": "Investigation",
    "stage.treatment": "Medical Treatment",
    "stage.demand_sent": "Demand sent",
    "stage.negotiation": "Negotiation",
    "stage.settlement": "Settlement / Suit filed",
    "stage.closed": "Closed",
    "stage.current": "Current step",

    // Attorney portal
    "att.portal.title": "Attorney portal",
    "att.portal.subtitle": "Your assigned client cases",
    "att.portal.view_site": "View site",
    "att.stats.open": "Open cases",
    "att.stats.total": "Total cases",
    "att.stats.treatment": "In treatment",
    "att.stats.settlement": "Settlement",
    "att.filter.search_placeholder": "Search by name, email, phone, city…",
    "att.filter.all_stages": "All stages",
    "att.empty.title": "No cases assigned yet",
    "att.empty.body": "When an admin assigns a lead or your covered city matches, the case appears here.",
    "att.auto_assigned": "Auto by city",
    "att.case.back": "Back to all cases",
    "att.case.client": "Client",
    "att.case.accident": "Accident type",
    "att.case.fault": "At fault",
    "att.case.police_report": "Police report filed",
    "att.case.injuries": "Reported injuries",
    "att.case.notes_client": "Client narrative",
    "att.case.ai_take": "AI assessment",
    "att.stage.title": "Case stage",
    "att.stage.subtitle": "Tap a stage to update. Last update",
    "att.stage.updated": "Stage updated to {stage}",
    "att.tab.overview": "Overview",
    "att.tab.treatments": "Treatments",
    "att.tab.notes": "Private notes",
    "att.tab.messages": "Client messages",
    "att.tab.documents": "Documents",
    "att.tab.history": "Stage history",
    "att.treatments.visits": "Total visits",
    "att.treatments.facilities": "Facilities",
    "att.treatments.empty": "No treatments logged yet. Ask the client to add visits in their portal.",
    "att.treatments.detailed": "Detailed visit log",
    "att.treatments.reviewed": "Reviewed",
    "att.treatments.mark_reviewed": "Mark reviewed",
    "att.notes.privacy": "Private — visible to attorneys only. The client cannot see these notes.",
    "att.notes.placeholder": "Strategy note, follow-up reminder, lien tracking…",
    "att.notes.empty": "No private notes yet.",
    "att.notes.confirm_delete": "Delete this note?",
    "att.messages.placeholder": "Reply to client…",
    "att.messages.empty": "No messages yet — start the conversation with your client.",
    "att.documents.empty": "No documents uploaded by the client yet.",
    "att.documents.download": "Download",
    "att.history.empty": "No stage changes recorded yet.",
    "att.accept.invalid": "This invite is invalid or expired. Ask your firm admin to resend it.",
    "att.accept.weak_pwd": "Password must be at least 8 characters.",
    "att.accept.mismatch": "Passwords do not match.",
    "att.accept.fail": "Could not accept invite. Please try again.",
    "att.accept.intro": "Set your password to access cases assigned to you. We auto-route leads to you based on the towns you cover.",
    "att.accept.disclaimer": "By accepting, you agree to keep client data confidential per HIPAA & state ethics rules.",
    "att.accept.loading": "Validating your invite…",
    "att.accept.welcome": "Welcome, {name}",
    "att.accept.subtitle": "Set a password to access your {firm} portal.",
    "att.accept.email_label": "Your account email",
    "att.accept.password": "New password",
    "att.accept.password_hint": "At least 8 characters. Use a mix of letters, numbers, symbols.",
    "att.accept.confirm": "Confirm password",
    "att.accept.submitting": "Creating account…",
    "att.accept.cta": "Activate my account",
    "att.accept.error_title": "Invite unavailable",
    "att.accept.go_login": "Go to login",

    // Claimant view of attorney
    "mc.attorney.pending_title": "Attorney being assigned",
    "mc.attorney.pending_body": "Our intake team is matching your case with a vetted attorney now. You'll see their name + direct contact here within an hour.",
    "mc.attorney.your_attorney": "Your attorney",
    "mc.attorney.matched": "Matched",
    "mc.attorney.current_stage": "Where your case stands today",

    // Compliance + Health
    "compliance.loading": "Loading your compliance score…",
    "compliance.title": "Treatment compliance",
    "compliance.claimant.title": "Your treatment progress",
    "compliance.score": "Compliance",
    "compliance.visits": "Visits completed",
    "compliance.actual_vs_expected": "Actual vs expected to date",
    "compliance.plan": "Plan",
    "compliance.week_short": "wk",
    "compliance.weeks_progress": "Week {done} of {total}",
    "compliance.health_breakdown": "Case health breakdown",
    "compliance.label.compliance": "Compliance",
    "compliance.label.velocity": "Stage velocity",
    "compliance.label.recency": "Recent activity",
    "compliance.status.excellent": "Excellent compliance",
    "compliance.status.good": "Good compliance",
    "compliance.status.at_risk": "At risk — needs attention",
    "compliance.status.critical": "Critical — case at risk",
    "compliance.status.not_started": "Plan set — visits starting soon",
    "compliance.status.no_plan": "No treatment plan yet",
    "compliance.no_plan": "Set a treatment plan to start scoring adherence. The plan should match what the treating physician recommended.",
    "compliance.claimant.no_plan": "Once your attorney sets your treatment plan, you'll see a live score here. The more consistently you attend appointments, the higher your case value.",
    "compliance.tip.excellent": "Outstanding — keep this rhythm. This is the strongest signal an insurer can see.",
    "compliance.tip.good": "On track — stay consistent to maintain leverage in negotiation.",
    "compliance.tip.at_risk": "Catch up on missed visits soon — insurers use treatment gaps to lowball offers.",
    "compliance.tip.critical": "Multiple missed visits — schedule and attend immediately to preserve case value.",
    "compliance.tip.not_started": "Plan is set. Schedule the first visit to start the clock.",
    "compliance.tip.no_plan": "Define visits/week + duration so we can track adherence.",
    "compliance.tip.claimant.excellent": "Amazing work — your consistency is making your case stronger every week.",
    "compliance.tip.claimant.good": "Great pace — keep showing up to every appointment.",
    "compliance.tip.claimant.at_risk": "Heads up — you've missed a few visits. Reschedule today; it directly impacts your settlement.",
    "compliance.tip.claimant.critical": "Important — please call your provider today to get back on track. Treatment gaps cost real money.",
    "compliance.gap_alert_title": "Treatment gap detected",
    "compliance.gap_alert_body": "{days} days since the last visit. Schedule one this week to keep the case strong.",

    // Health score
    "health.tooltip": "Case health score: {score}/100",
    "health.band.strong": "Strong",
    "health.band.ok": "OK",
    "health.band.watch": "Watch",
    "health.band.risk": "At risk",

    // Tasks
    "tasks.loading": "Loading tasks…",
    "tasks.empty_title": "No tasks yet",
    "tasks.empty_body": "Tasks auto-generate as your case moves between stages. You can also add ad-hoc ones.",
    "tasks.heading.pending": "Pending",
    "tasks.heading.done": "Completed",
    "tasks.add_placeholder": "Add a task for this case…",
    "tasks.workflow_pill": "Auto",
    "tasks.confirm_delete": "Delete this task?",
    "tasks.overdue": "Overdue",
    "tasks.due_in_hours": "Due in {n}h",
    "tasks.due_in_days": "Due in {n}d",
    "tasks.prio.low": "Low",
    "tasks.prio.medium": "Medium",
    "tasks.prio.high": "High",
    "tasks.prio.urgent": "Urgent",
    "tasks.due_opt.1h": "1 hour",
    "tasks.due_opt.1d": "1 day",
    "tasks.due_opt.3d": "3 days",
    "tasks.due_opt.1w": "1 week",
    "tasks.due_opt.2w": "2 weeks",

    // Treatment plan dialog
    "plan.required": "Visits/week, duration, and start date are required.",
    "plan.saved": "Treatment plan saved",
    "plan.title": "Set treatment plan",
    "plan.intro": "Match what the treating physician recommended. We use this to score adherence and flag treatment gaps.",
    "plan.visits_per_week": "Visits per week",
    "plan.duration_weeks": "Duration (weeks)",
    "plan.start_date": "Start date",
    "plan.notes": "Notes (optional)",
    "plan.notes_placeholder": "e.g., PT 2x/wk for 12 weeks per Dr. Cohen + monthly orthopedic check-ins.",
    "plan.cancel": "Cancel",
    "plan.save": "Save plan",
    "plan.saving": "Saving…",

    // Attorney plan inline CTAs
    "att.plan.set_cta": "Set treatment plan",
    "att.plan.edit_cta": "Edit treatment plan",
    "att.tasks.heading": "Workflow tasks",

    // Intake referral picker
    "intake.referral.label": "How'd you hear about us?",
    "intake.referral.organic": "Search",
    "intake.referral.google_ads": "Google ad",
    "intake.referral.doctor": "Doctor",
    "intake.referral.co_counsel": "Another lawyer",
    "intake.referral.client_referral": "Friend / past client",
    "intake.referral.billboard": "Billboard",
    "intake.referral.social": "Social media",
    "intake.referral.other": "Other",
    "intake.referral.pick_one": "Pick one…",
    "intake.referral.not_listed": "Not listed (type below)",
    "intake.referral.hint_placeholder": "Doctor or referrer name…",
  },

  es: {
    // Common
    "lang.label": "Idioma",
    "lang.english": "English",
    "lang.spanish": "Español",
    "se_habla": "Hablamos español · We speak Spanish",
    "free_review": "Revisión Gratuita",
    "back": "Atrás",
    "continue": "Continuar",
    "submit": "Enviar",
    "loading": "Cargando…",
    "close": "Cerrar",
    "save": "Guardar",
    "cancel": "Cancelar",

    // Nav
    "nav.case_value": "Valor del Caso",
    "nav.chat": "Chat de Admisión",
    "nav.hotline": "Línea 24/7",
    "nav.sign_in": "Iniciar sesión",
    "nav.my_cases": "Mis Casos",
    "nav.find_care": "Encontrar Atención",

    // Urgency banner
    "urgency.banner": "Plazo en NY: 3 años · Plazo en NJ: 2 años — cada día cuenta. Revisión gratuita en 60 segundos.",

    // Hero
    "hero.pill": "Víctimas de accidentes en NY · NJ",
    "hero.h1.line1": "¿Te golpearon en la carretera?",
    "hero.h1.line2": "Obtén el acuerdo",
    "hero.h1.line3": "que realmente mereces.",
    "hero.subhead": "Apex conecta a víctimas de accidentes automovilísticos en Nueva York y Nueva Jersey con abogados verificados de lesiones personales. Revisión gratuita. Sin pagos por adelantado. Disponible 24/7 — incluso desde tu cama de hospital.",
    "hero.cta.intake": "Obtén Tu Revisión Gratuita",
    "hero.cta.estimator": "Estima el Valor de Mi Caso",
    "hero.bullet.2min": "Admisión en 2 minutos",
    "hero.bullet.nofee": "Sin honorarios si no ganamos",
    "hero.bullet.confidential": "100% confidencial",
    "hero.stats.title": "En cifras",
    "hero.stat.recovered": "Recuperado para clientes",
    "hero.stat.cases": "Casos gestionados",
    "hero.stat.rating": "Calificación promedio",
    "hero.stat.hotline": "Línea de admisión",
    "hero.help_now": "¿Necesitas ayuda ahora?",

    // Trust strip
    "trust.featured": "Destacados en",

    // Tools quick nav
    "tools.intake.title": "Revisión Gratuita",
    "tools.intake.desc": "Formulario seguro de 2 min. Envíalo cuando quieras.",
    "tools.intake.cta": "Comenzar ahora",
    "tools.estimator.title": "Estimador IA del Valor",
    "tools.estimator.desc": "Rango de acuerdo conservador en segundos.",
    "tools.estimator.cta": "Estimar valor",
    "tools.chat.title": "Habla con Avery",
    "tools.chat.desc": "Cuéntanos sobre tu accidente con nuestra IA de admisión.",
    "tools.chat.cta": "Abrir chat",
    "tools.find_care.title": "Encuentra un Médico",
    "tools.find_care.desc": "Conexión con ortopedia y columna para cualquier lesión — no solo accidentes.",
    "tools.find_care.cta": "Conectarme",

    // Find Care promo (Landing section)
    "findcare.section_label": "¿Lesionado pero no por un choque?",
    "findcare.section_title": "También te ayudamos a encontrar médicos ortopédicos y de columna que aceptan tu seguro.",
    "findcare.section_body": "Cuéntanos tus síntomas y tu compañía de seguro. Te conectaremos con un proveedor ortopédico o de columna de confianza en Nueva York o Nueva Jersey — típicamente en 1 día hábil. Abierto para todos, esté o no relacionado con un accidente.",
    "findcare.section_cta": "Encuentra Atención",
    "findcare.tag.private": "Cifrado y privado",
    "findcare.tag.insurance": "Aceptamos la mayoría de seguros",
    "findcare.preview.label": "Cómo funciona",
    "findcare.preview.item1": "Marca tus síntomas (espalda, cuello, rodilla, etc.).",
    "findcare.preview.item2": "Selecciona tu compañía de seguro de una lista.",
    "findcare.preview.item3": "Te conectamos con un médico ortopédico o de columna.",
    "findcare.preview.item4": "Un coordinador te llama en 1 día hábil.",
    "findcare.preview.privacy_title": "Privacidad primero",
    "findcare.preview.privacy_body": "Nunca pedimos tu número de miembro. Nunca vendemos tu información. Tus datos se usan solo para conectarte con atención médica.",

    // Track section
    "track.label": "Tu caso, siempre a la vista",
    "track.title": "Inicia sesión para seguir el estado de tu caso en tiempo real.",
    "track.body": "Cada solicitud se conecta a tu portal privado. Ve exactamente dónde está tu caso — desde la admisión hasta el acuerdo — sin hacer una sola llamada.",
    "track.cta_signup": "Crear mi cuenta",
    "track.cta_login": "Iniciar sesión",
    "track.case_id_label": "Caso",
    "track.status.submitted": "Enviado",
    "track.status.contacted": "Abogado contactado",
    "track.status.accepted": "Caso aceptado",
    "track.status.resolved": "Resuelto",
    "track.latest_update": "Última actualización · hace 2 días",
    "track.latest_body": "Tu abogado asignado revisó tus registros médicos y te llamará mañana antes de las 5 PM EST.",

    // How it works
    "how.label": "Cómo funciona",
    "how.title": "Del accidente al acuerdo en tres pasos claros.",
    "how.step1.title": "Cuéntanos qué pasó",
    "how.step1.desc": "Envía una admisión de 2 minutos o chatea con Avery, nuestra asistente de IA. Solo pedimos lo necesario.",
    "how.step2.title": "Te conectamos",
    "how.step2.desc": "En 1 hora hábil, un abogado verificado de NY/NJ revisa tu caso — gratis.",
    "how.step3.title": "Cobra",
    "how.step3.desc": "Si no ganas, no pagas nada. Tiempo promedio de acuerdo: 4–9 meses.",

    // Why us
    "why.label": "Te entendemos",
    "why.title": "No deberías pelear solo contra la aseguradora.",
    "why.body": "Después de un choque en el BQE, el Turnpike, o en cualquier lugar de NY/NJ, las llamadas empiezan rápido y las ofertas son insultantemente bajas. Estamos aquí para que te concentres en sanar mientras un abogado real maneja cada conversación, plazo y embargo por ti.",
    "why.feature.vetted": "Abogados verificados",
    "why.feature.response": "Respuesta en 1 hora",
    "why.feature.contingency": "Solo por contingencia",

    // Testimonial
    "testimonial.quote": "Me chocaron por detrás en la LIE con dos discos herniados y sin saber qué hacer. Apex me conectó con un abogado esa misma tarde. Ocho meses después tenía un acuerdo de seis cifras y cero estrés.",
    "testimonial.attribution": "— Marcus T., Queens NY · colisión auto, 2024",

    // FAQ
    "faq.label": "Preguntas frecuentes",
    "faq.title": "Preguntas comunes, respuestas directas.",
    "faq.q1": "¿Cuánto cuesta hablar con ustedes?",
    "faq.a1": "Nada. La revisión del caso y la asignación de abogado son 100% gratis. Si tu abogado toma el caso, solo pagas si ganas — contingencia estándar, sin costo por adelantado.",
    "faq.q2": "¿Y si el accidente no fue totalmente culpa del otro?",
    "faq.a2": "Tanto NY como NJ permiten recuperar daños incluso si compartes la culpa (negligencia comparativa). Queremos escuchar tu historia de todos modos.",
    "faq.q3": "Ya vi a un abogado. ¿Puedo cambiar?",
    "faq.a3": "Sí. Eres libre de cambiar de representación en cualquier momento. Podemos ayudarte a encontrar uno mejor.",
    "faq.q4": "¿Mi información es privada?",
    "faq.a4": "Absolutamente. Las solicitudes están cifradas, nunca se venden, y solo se comparten con el abogado verificado que revisa tu caso.",
    "faq.q5": "¿Cuánto tiempo tengo para presentar el caso?",
    "faq.a5": "En NY el plazo es generalmente 3 años; en NJ son 2. No esperes — la evidencia se desvanece rápido.",

    // Final CTA
    "final.title": "¿Herido en un choque? No esperes.",
    "final.body": "La mayoría de los casos MVA en NY/NJ se resuelven en 12 meses cuando se presentan a tiempo. Empecemos el tuyo.",
    "final.cta.intake": "Comienza la Revisión Gratuita",
    "final.cta.chat": "Habla con Avery",

    // Footer
    "footer.about": "Conectamos a víctimas de accidentes automovilísticos en Nueva York y Nueva Jersey con abogados experimentados — sin costos por adelantado. Disponible 24/7.",
    "footer.tag.ny": "Licenciados en NY",
    "footer.tag.nj": "Licenciados en NJ",
    "footer.tag.no_fee": "Sin honorarios si no ganamos",
    "footer.tag.spanish": "Hablamos español",
    "footer.action.title": "Actúa Ya",
    "footer.action.intake": "Comenzar Revisión Gratuita",
    "footer.action.estimator": "Estimador IA del Valor",
    "footer.action.chat": "Hablar con Avery (IA)",
    "footer.action.call": "Llamar a Línea 24/7",
    "footer.legal.title": "Legal",
    "footer.legal.advertising": "Publicidad de abogados",
    "footer.legal.no_guarantee": "Resultados pasados no garantizan resultados futuros",
    "footer.legal.consult": "Se requiere consulta previa antes de la representación",
    "footer.legal.admin": "Acceso de Administrador",
    "footer.rights": "Todos los derechos reservados.",
    "footer.serving": "Atendiendo Nueva York y Nueva Jersey · Disponible 24/7",

    // Sticky mobile CTA
    "mobile.call": "Llamar",

    // Lead intake form
    "intake.label": "Revisión gratuita",
    "intake.title": "Cuéntanos sobre tu accidente.",
    "intake.subtitle": "Toma ~2 minutos. Solo pedimos lo necesario.",
    "intake.step": "Paso {n} de {total} · {label}",
    "intake.step.accident": "Accidente",
    "intake.step.injuries": "Lesiones",
    "intake.step.contact": "Contacto",
    "intake.encrypted": "Cifrado",
    "intake.state_label": "Estado del accidente",
    "intake.state_ny": "Nueva York",
    "intake.state_nj": "Nueva Jersey",
    "intake.date_label": "Fecha del accidente",
    "intake.city_label": "Ciudad / municipio",
    "intake.city_placeholder": "ej. Brooklyn, Newark",
    "intake.type_label": "Tipo de accidente",
    "intake.select": "Selecciona…",
    "intake.type.rear_end": "Colisión trasera",
    "intake.type.head_on": "Colisión frontal",
    "intake.type.tbone": "Colisión lateral / en T",
    "intake.type.pileup": "Choque múltiple",
    "intake.type.hit_run": "Choque y huida",
    "intake.type.pedestrian": "Peatón atropellado",
    "intake.type.motorcycle": "Accidente de motocicleta",
    "intake.type.rideshare": "Uber/Lyft",
    "intake.type.truck": "Camión / tráiler",
    "intake.type.other": "Otro",
    "intake.fault_label": "¿Quién crees que tuvo la culpa?",
    "intake.fault.other": "El otro conductor",
    "intake.fault.me": "Yo",
    "intake.fault.shared": "Culpa compartida",
    "intake.fault.unsure": "No estoy seguro",
    "intake.police_label": "Se presentó un reporte policial",
    "intake.injuries_label": "Describe tus lesiones",
    "intake.injuries_placeholder": "Dolor de cuello, discos herniados, fracturas, etc.",
    "intake.treatment_label": "He recibido tratamiento médico por estas lesiones",
    "intake.has_attorney_label": "Ya tengo un abogado en este caso",
    "intake.notes_label": "¿Algo más que debamos saber?",
    "intake.notes_placeholder": "Opcional. Testigos, daños al vehículo, lesiones previas…",
    "intake.name_label": "Nombre completo",
    "intake.email_label": "Correo electrónico",
    "intake.phone_label": "Teléfono",
    "intake.phone_placeholder": "(555) 555-1234",
    "intake.consent": "Al enviar, aceptas ser contactado por Apex MVA y un abogado asociado. Tu información es confidencial y nunca se vende.",
    "intake.submit": "Enviar Revisión Gratuita",
    "intake.submitting": "Enviando…",
    "intake.success.title": "Lo recibimos. Respira.",
    "intake.success.body": "Un abogado verificado licenciado en {state} revisará tu caso y te contactará en 1 hora hábil. Si tu situación es urgente, llama a nuestra línea 24/7.",
    "intake.success.call": "Llamar",
    "intake.success.home": "Volver al Inicio",
    "intake.toast.success": "Revisión enviada. Te contactaremos pronto.",

    // Chatbot
    "chat.label": "Asistente IA de Admisión",
    "chat.title": "Chatea con Avery.",
    "chat.subtitle": "Una conversación cifrada y sin juicio. Avery no es abogada — pero te ayuda a organizar los hechos para conectarte más rápido.",
    "chat.header.subtitle": "Apex MVA · IA de admisión · siempre confidencial",
    "chat.input_placeholder": "Escribe lo que pasó…",
    "chat.first_message": "Hola, soy Avery — la asistente de admisión de Apex. Lamento mucho lo que estás pasando. Cuando estés listo/a, ¿puedes contarme un poco sobre lo que pasó?",
    "chat.skip": "Saltar el chat — envía el formulario seguro",
    "chat.or_call": "O llama al",
    "chat.error": "Lo siento, tuve un problema para responder. Por favor intenta de nuevo o llama a nuestra línea.",

    // Estimator
    "est.label": "Estimador IA del Valor",
    "est.title": "Obtén una lectura rápida de tu caso — con IA.",
    "est.body": "Ingresa los detalles de tu accidente. Generaremos un rango de acuerdo conservador para que llegues a tu llamada con expectativas realistas.",
    "est.not_advice": "No es asesoría legal.",
    "est.injuries_label": "Lesiones sufridas",
    "est.injuries_placeholder": "ej. latigazo, hernia L5-S1, conmoción, muñeca rota",
    "est.fault_label": "Culpa",
    "est.treatment_label": "Recibí tratamiento médico",
    "est.notes_label": "Detalles adicionales (opcional)",
    "est.notes_placeholder": "Salarios perdidos, terapia continua, daños al vehículo, etc.",
    "est.submit": "Estimar el Valor de Mi Caso",
    "est.analyzing": "Analizando…",
    "est.placeholder.body": "Tu rango estimado aparecerá aquí. La mayoría tarda 5–10 segundos.",
    "est.placeholder.loading": "Avery está revisando tu caso…",
    "est.range_label": "Rango estimado del acuerdo",
    "est.strength_label": "Fortaleza del caso:",
    "est.cta_match": "Conéctame con un abogado",
    "est.disclaimer_fallback": "Las estimaciones son orientativas y no constituyen asesoría legal.",

    // Login
    "login.back": "Volver al sitio",
    "login.tagline.admin": "Gestión de leads para equipos de admisión de lesiones personales en NY y NJ.",
    "login.tagline.claimant": "Sigue tu caso, recibe actualizaciones y mantente al tanto con tu abogado.",
    "login.footer.admin": "Solo personal autorizado.",
    "login.footer.claimant": "Tu información es confidencial y está cifrada.",
    "login.title.admin": "Acceso de Administrador",
    "login.title.claimant": "Inicia sesión para seguir tu caso",
    "login.subtitle.admin": "Accede al panel de gestión de leads.",
    "login.subtitle.claimant": "Bienvenido de vuelta. Veamos tu caso.",
    "login.submit": "Iniciar sesión",
    "login.submitting": "Iniciando sesión…",
    "login.new_here": "¿Nuevo aquí?",
    "login.create_account": "Crear una cuenta",

    // Register
    "reg.title_panel": "Crea tu cuenta del caso.",
    "reg.tagline_panel": "Sigue las actualizaciones, adjunta documentos y envía mensajes a tu abogado — todo en un solo lugar.",
    "reg.bullet.encrypted": "100% cifrado y confidencial",
    "reg.bullet.nofee": "Sin honorarios si no ganamos",
    "reg.bullet.licensed": "Socios licenciados en NY y NJ",
    "reg.never_sold": "La información aquí compartida nunca se vende.",
    "reg.title": "Crear cuenta",
    "reg.subtitle": "Toma 30 segundos. ¿Ya enviaste un caso? Lo vincularemos automáticamente.",
    "reg.name": "Nombre completo",
    "reg.email": "Correo electrónico",
    "reg.phone": "Teléfono (opcional)",
    "reg.password": "Contraseña",
    "reg.password_too_short": "La contraseña debe tener al menos 6 caracteres.",
    "reg.submit": "Crear cuenta",
    "reg.submitting": "Creando…",
    "reg.have_account": "¿Ya tienes cuenta?",
    "reg.sign_in": "Inicia sesión",

    // MyCases
    "mc.label": "Tu portal",
    "mc.welcome": "Bienvenido, {name}.",
    "mc.subtitle": "Aquí está el estado de cada caso que has enviado con Apex.",
    "mc.new_case": "Enviar caso nuevo",
    "mc.logout": "Cerrar sesión",
    "mc.loading": "Cargando tus casos…",
    "mc.empty.title": "Aún no hay casos",
    "mc.empty.body": "Comienza una revisión gratuita y te conectaremos con un abogado verificado de NY/NJ.",
    "mc.empty.cta": "Iniciar mi revisión gratuita",
    "mc.tab.overview": "Resumen",
    "mc.tab.treatments": "Tratamientos",
    "mc.tab.documents": "Documentos",
    "mc.tab.messages": "Mensajes",
    "mc.detail.submitted": "Enviado",
    "mc.detail.fault": "Culpa",
    "mc.detail.injuries": "Lesiones",
    "mc.detail.notes": "Notas",
    "mc.detail.call": "Llamar al equipo de admisión",
    "mc.status.new": "Enviado",
    "mc.status.contacted": "Abogado contactado",
    "mc.status.qualified": "Caso aceptado",
    "mc.status.closed": "Resuelto",
    "mc.status.rejected": "No es compatible",
    "mc.rejected_msg": "Lamentablemente tu caso no fue compatible esta vez. Nuestro equipo puede referirte a otros recursos — contáctanos cuando quieras.",
    "mc.fallback_title": "Accidente automovilístico",

    // Treatments
    "tx.total": "Visitas totales",
    "tx.completed": "Completadas",
    "tx.physicians": "Médicos",
    "tx.record": "Registro de tratamiento",
    "tx.add": "Agregar visita",
    "tx.cancel": "Cancelar",
    "tx.physician": "Nombre del médico",
    "tx.physician_placeholder": "Dr./Dra. Apellido",
    "tx.specialty": "Especialidad",
    "tx.specialty_placeholder": "Ortopedista, Quiropráctico…",
    "tx.facility": "Clínica / centro",
    "tx.facility_placeholder": "NY Spine Center",
    "tx.visit_date": "Fecha de la visita",
    "tx.visit_type": "Tipo de visita",
    "tx.notes": "Notas",
    "tx.notes_placeholder": "Diagnóstico, recetas, próximos pasos…",
    "tx.occurred": "La visita ya ocurrió (desmarca si está agendada / próxima)",
    "tx.save": "Guardar visita",
    "tx.saving": "Guardando…",
    "tx.loading": "Cargando historial de tratamientos…",
    "tx.empty": "Aún no hay visitas registradas. Usa {add_visit} arriba para empezar tu registro.",
    "tx.empty.add_visit": "Agregar visita",
    "tx.upcoming": "Próxima",
    "tx.added_by_team": "Equipo Apex",
    "tx.added_by_you": "tú",
    "tx.required": "El nombre del médico es obligatorio.",
    "tx.toast.saved": "Visita agregada a tu registro.",
    "tx.toast.removed": "Visita eliminada.",
    "tx.delete_confirm": "¿Eliminar visita con {physician} el {date}?",
    "tx.visit_count_one": "{n} visita",
    "tx.visit_count_other": "{n} visitas",

    // Documents
    "docs.add.title": "Agregar un documento",
    "docs.add.body": "Reportes policiales, facturas médicas, fotos. PDF/JPG/PNG/HEIC hasta 10 MB.",
    "docs.upload": "Subir archivo",
    "docs.uploading": "Subiendo…",
    "docs.loading": "Cargando documentos…",
    "docs.empty": "Aún no hay documentos.",
    "docs.view": "Ver",
    "docs.uploaded_by": "subido por",
    "docs.uploader.team": "Equipo Apex",
    "docs.uploader.you": "tú",
    "docs.too_large": "Archivo demasiado grande (máx 10MB).",
    "docs.uploaded": "Subido.",
    "docs.deleted": "Eliminado.",
    "docs.delete_confirm": "¿Eliminar \"{filename}\"?",

    // Messages
    "msg.thread.claimant": "Hilo de mensajes con tu equipo de admisión Apex",
    "msg.thread.admin": "Hilo de mensajes con el reclamante",
    "msg.loading": "Cargando…",
    "msg.empty.claimant": "Tu equipo de admisión Apex te escribirá aquí cuando haya actualizaciones.",
    "msg.empty.admin": "Aún no hay mensajes — inicia la conversación.",
    "msg.input.claimant": "Escribe un mensaje al equipo de admisión…",
    "msg.input.admin": "Responder al reclamante…",
    "msg.sender.you": "Tú",
    "msg.sender.team": "Equipo de Admisión Apex",
    "msg.sender.claimant": "Reclamante",

    // Speed-to-lead promise
    "speed.promise": "Te llamamos en 5 minutos — o tu revisión es gratis.",

    // Social proof ticker
    "social.action.intake": "comenzó una revisión gratuita",
    "social.action.estimate": "obtuvo una estimación IA",
    "social.action.match": "fue conectado con un abogado",
    "social.action.settled": "recibió un acuerdo",

    // Statute countdown
    "statute.title": "Tiempo restante para presentar tu reclamo",
    "statute.expired": "El plazo legal venció — llámanos de todas formas, aún podemos ayudar.",
    "statute.urgent": "Urgente — presenta lo antes posible.",
    "statute.years_one": "{n} año",
    "statute.years_other": "{n} años",
    "statute.months_one": "{n} mes",
    "statute.months_other": "{n} meses",
    "statute.days_one": "{n} día",
    "statute.days_other": "{n} días",

    // Voice intake
    "voice.intake.cta": "Toca para hablar en vez de escribir",
    "voice.recording": "Grabando — toca para detener",
    "voice.transcribing": "Transcribiendo tu historia…",
    "voice.transcribed": "Listo — revisa y edita abajo.",
    "voice.error": "No pudimos transcribir. Por favor escribe tu historia.",
    "voice.permission_denied": "Acceso al micrófono bloqueado. Escribe en su lugar.",

    // Magic link
    "magic.tab.password": "Usar contraseña",
    "magic.tab.magic": "Envíame un enlace",
    "magic.subtitle": "Recibe un enlace único de acceso por correo (y SMS si tenemos tu número). Sin contraseña.",
    "magic.email_placeholder": "tu@correo.com",
    "magic.send": "Enviarme el enlace",
    "magic.sending": "Enviando…",
    "magic.sent.title": "Revisa tu correo y teléfono.",
    "magic.sent.body": "Si existe una cuenta para {email}, recibirás un enlace único de acceso en 1 minuto. Expira en 15 minutos.",
    "magic.sent.again": "Enviar otro enlace",
    "magic.verify.title": "Iniciando sesión…",
    "magic.verify.success": "Bienvenido de vuelta. Redirigiendo…",
    "magic.verify.error": "Este enlace es inválido o expiró. Solicita uno nuevo.",
    "magic.verify.cta": "Solicitar enlace nuevo",

    // Sticky mobile CTA
    "mobile.text": "Envíanos un texto",

    // City landing
    "city.headline": "¿Lesionado en un accidente en {city}?",
    "city.subhead": "Revisión gratuita · Sin honorarios si no ganamos · Abogados licenciados en NY y NJ, disponibles 24/7.",
    "city.local_pill": "Local · {city}",
    "city.body": "Nuestra red de abogados verificados de lesiones personales recupera millones para víctimas de accidentes en {city} cada año. Desde {landmarks} — conocemos las carreteras, los tribunales y las aseguradoras.",
    "city.cta_intake": "Comenzar Revisión Gratuita",
    "city.cta_chat": "Hablar con Avery",
    "city.trust1": "Respuesta en 1 hora · 24/7",
    "city.trust2": "Hablamos español",
    "city.trust3": "Sin costos por adelantado",

    // Common
    "yes": "Sí",
    "no": "No",

    "nav.attorney_portal": "Portal de abogados",

    "stage.intake": "Admisión",
    "stage.investigation": "Investigación",
    "stage.treatment": "Tratamiento médico",
    "stage.demand_sent": "Demanda enviada",
    "stage.negotiation": "Negociación",
    "stage.settlement": "Acuerdo / Demanda presentada",
    "stage.closed": "Cerrado",
    "stage.current": "Etapa actual",

    "att.portal.title": "Portal de abogados",
    "att.portal.subtitle": "Casos de tus clientes asignados",
    "att.portal.view_site": "Ver sitio",
    "att.stats.open": "Casos abiertos",
    "att.stats.total": "Total de casos",
    "att.stats.treatment": "En tratamiento",
    "att.stats.settlement": "Acuerdo",
    "att.filter.search_placeholder": "Buscar por nombre, email, teléfono, ciudad…",
    "att.filter.all_stages": "Todas las etapas",
    "att.empty.title": "Aún no tienes casos asignados",
    "att.empty.body": "Cuando un administrador asigne un caso o tu ciudad cubierta coincida, aparecerá aquí.",
    "att.auto_assigned": "Auto por ciudad",
    "att.case.back": "Volver a todos los casos",
    "att.case.client": "Cliente",
    "att.case.accident": "Tipo de accidente",
    "att.case.fault": "Quién tuvo la culpa",
    "att.case.police_report": "Reporte policial",
    "att.case.injuries": "Lesiones reportadas",
    "att.case.notes_client": "Relato del cliente",
    "att.case.ai_take": "Evaluación IA",
    "att.stage.title": "Etapa del caso",
    "att.stage.subtitle": "Toca una etapa para actualizar. Última actualización",
    "att.stage.updated": "Etapa actualizada a {stage}",
    "att.tab.overview": "Resumen",
    "att.tab.treatments": "Tratamientos",
    "att.tab.notes": "Notas privadas",
    "att.tab.messages": "Mensajes al cliente",
    "att.tab.documents": "Documentos",
    "att.tab.history": "Historial de etapas",
    "att.treatments.visits": "Total de visitas",
    "att.treatments.facilities": "Centros",
    "att.treatments.empty": "Aún no hay tratamientos. Pide al cliente que registre sus visitas.",
    "att.treatments.detailed": "Registro detallado de visitas",
    "att.treatments.reviewed": "Revisado",
    "att.treatments.mark_reviewed": "Marcar revisado",
    "att.notes.privacy": "Privado — solo visible para abogados. El cliente no puede ver estas notas.",
    "att.notes.placeholder": "Nota estratégica, recordatorio, seguimiento de gravamen…",
    "att.notes.empty": "Aún no hay notas privadas.",
    "att.notes.confirm_delete": "¿Eliminar esta nota?",
    "att.messages.placeholder": "Responder al cliente…",
    "att.messages.empty": "Aún no hay mensajes — inicia la conversación con tu cliente.",
    "att.documents.empty": "El cliente aún no ha subido documentos.",
    "att.documents.download": "Descargar",
    "att.history.empty": "Aún no hay cambios de etapa registrados.",
    "att.accept.invalid": "Esta invitación es inválida o expiró. Pide a tu administrador que la reenvíe.",
    "att.accept.weak_pwd": "La contraseña debe tener al menos 8 caracteres.",
    "att.accept.mismatch": "Las contraseñas no coinciden.",
    "att.accept.fail": "No pudimos aceptar la invitación. Intenta de nuevo.",
    "att.accept.intro": "Establece tu contraseña para acceder a los casos asignados. Te enrutamos clientes automáticamente según las ciudades que cubres.",
    "att.accept.disclaimer": "Al aceptar, te comprometes a mantener la confidencialidad de los datos del cliente conforme a HIPAA y la ética estatal.",
    "att.accept.loading": "Validando tu invitación…",
    "att.accept.welcome": "Bienvenido, {name}",
    "att.accept.subtitle": "Establece una contraseña para acceder a tu portal de {firm}.",
    "att.accept.email_label": "Tu correo de cuenta",
    "att.accept.password": "Nueva contraseña",
    "att.accept.password_hint": "Al menos 8 caracteres. Usa letras, números y símbolos.",
    "att.accept.confirm": "Confirmar contraseña",
    "att.accept.submitting": "Creando cuenta…",
    "att.accept.cta": "Activar mi cuenta",
    "att.accept.error_title": "Invitación no disponible",
    "att.accept.go_login": "Ir al login",

    "mc.attorney.pending_title": "Asignando abogado",
    "mc.attorney.pending_body": "Nuestro equipo de admisión está conectando tu caso con un abogado verificado. Verás su nombre y contacto directo aquí en menos de una hora.",
    "mc.attorney.your_attorney": "Tu abogado",
    "mc.attorney.matched": "Asignado",
    "mc.attorney.current_stage": "Etapa actual de tu caso",

    // Compliance + Health
    "compliance.loading": "Cargando tu puntuación de cumplimiento…",
    "compliance.title": "Cumplimiento de tratamiento",
    "compliance.claimant.title": "Tu progreso de tratamiento",
    "compliance.score": "Cumplimiento",
    "compliance.visits": "Visitas completadas",
    "compliance.actual_vs_expected": "Reales vs esperadas hasta hoy",
    "compliance.plan": "Plan",
    "compliance.week_short": "sem",
    "compliance.weeks_progress": "Semana {done} de {total}",
    "compliance.health_breakdown": "Desglose de salud del caso",
    "compliance.label.compliance": "Cumplimiento",
    "compliance.label.velocity": "Velocidad de etapa",
    "compliance.label.recency": "Actividad reciente",
    "compliance.status.excellent": "Excelente cumplimiento",
    "compliance.status.good": "Buen cumplimiento",
    "compliance.status.at_risk": "En riesgo — requiere atención",
    "compliance.status.critical": "Crítico — caso en riesgo",
    "compliance.status.not_started": "Plan establecido — visitas próximas",
    "compliance.status.no_plan": "Sin plan de tratamiento aún",
    "compliance.no_plan": "Establece un plan de tratamiento para empezar a puntuar adherencia. Debe coincidir con lo recomendado por el médico tratante.",
    "compliance.claimant.no_plan": "Cuando tu abogado establezca tu plan, verás un puntaje en vivo aquí. Mientras más consistente asistas a las citas, mayor será el valor de tu caso.",
    "compliance.tip.excellent": "Excelente — mantén este ritmo. Es la señal más fuerte para una aseguradora.",
    "compliance.tip.good": "Vas bien — mantén la consistencia para preservar tu poder de negociación.",
    "compliance.tip.at_risk": "Ponte al día con las visitas perdidas pronto — las aseguradoras usan los vacíos para bajar la oferta.",
    "compliance.tip.critical": "Múltiples visitas perdidas — agenda y asiste de inmediato para preservar el valor del caso.",
    "compliance.tip.not_started": "Plan establecido. Agenda la primera visita para iniciar el conteo.",
    "compliance.tip.no_plan": "Define visitas/semana + duración para poder dar seguimiento.",
    "compliance.tip.claimant.excellent": "Trabajo increíble — tu constancia fortalece tu caso cada semana.",
    "compliance.tip.claimant.good": "Buen ritmo — sigue asistiendo a cada cita.",
    "compliance.tip.claimant.at_risk": "Atención — has perdido varias visitas. Reagenda hoy; impacta directamente tu acuerdo.",
    "compliance.tip.claimant.critical": "Importante — llama a tu proveedor hoy para retomar el tratamiento. Los vacíos cuestan dinero real.",
    "compliance.gap_alert_title": "Vacío en tratamiento detectado",
    "compliance.gap_alert_body": "{days} días desde la última visita. Agenda una esta semana para mantener el caso fuerte.",

    "health.tooltip": "Salud del caso: {score}/100",
    "health.band.strong": "Fuerte",
    "health.band.ok": "OK",
    "health.band.watch": "Atención",
    "health.band.risk": "Riesgo",

    "tasks.loading": "Cargando tareas…",
    "tasks.empty_title": "Aún no hay tareas",
    "tasks.empty_body": "Las tareas se generan automáticamente al avanzar de etapa. También puedes agregar las tuyas.",
    "tasks.heading.pending": "Pendientes",
    "tasks.heading.done": "Completadas",
    "tasks.add_placeholder": "Agrega una tarea para este caso…",
    "tasks.workflow_pill": "Auto",
    "tasks.confirm_delete": "¿Eliminar esta tarea?",
    "tasks.overdue": "Vencida",
    "tasks.due_in_hours": "Vence en {n}h",
    "tasks.due_in_days": "Vence en {n}d",
    "tasks.prio.low": "Baja",
    "tasks.prio.medium": "Media",
    "tasks.prio.high": "Alta",
    "tasks.prio.urgent": "Urgente",
    "tasks.due_opt.1h": "1 hora",
    "tasks.due_opt.1d": "1 día",
    "tasks.due_opt.3d": "3 días",
    "tasks.due_opt.1w": "1 semana",
    "tasks.due_opt.2w": "2 semanas",

    "plan.required": "Visitas/semana, duración y fecha de inicio son obligatorios.",
    "plan.saved": "Plan de tratamiento guardado",
    "plan.title": "Establecer plan de tratamiento",
    "plan.intro": "Refleja lo recomendado por el médico tratante. Usamos esto para puntuar adherencia y detectar vacíos.",
    "plan.visits_per_week": "Visitas por semana",
    "plan.duration_weeks": "Duración (semanas)",
    "plan.start_date": "Fecha de inicio",
    "plan.notes": "Notas (opcional)",
    "plan.notes_placeholder": "Ej., Fisio 2x/sem por 12 semanas según Dr. Cohen + chequeo ortopédico mensual.",
    "plan.cancel": "Cancelar",
    "plan.save": "Guardar plan",
    "plan.saving": "Guardando…",

    "att.plan.set_cta": "Establecer plan",
    "att.plan.edit_cta": "Editar plan",
    "att.tasks.heading": "Tareas del flujo",

    "intake.referral.label": "¿Cómo nos conociste?",
    "intake.referral.organic": "Búsqueda",
    "intake.referral.google_ads": "Anuncio de Google",
    "intake.referral.doctor": "Médico",
    "intake.referral.co_counsel": "Otro abogado",
    "intake.referral.client_referral": "Amigo / cliente previo",
    "intake.referral.billboard": "Valla publicitaria",
    "intake.referral.social": "Redes sociales",
    "intake.referral.other": "Otro",
    "intake.referral.pick_one": "Elige uno…",
    "intake.referral.not_listed": "No aparece (escribe abajo)",
    "intake.referral.hint_placeholder": "Nombre del médico o referente…",
  },
};
